import { DeleteOutline, EventBusyOutlined, WarningAmberOutlined } from '@mui/icons-material';
import { Box, Button, IconButton, Tooltip, Typography, styled } from '@mui/material';
import moment from 'moment';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Column } from 'react-table';
import { Dialog, Table, useLoader } from 'thermia-react-container';
import useApiClient from '../../api/api-client';
import { ApiKey } from '../../api/api-types';
import ErrorComponent from '../../components/error-component';

const isKeyExpired = (expires: Date): boolean => !!expires && moment(expires).isBefore(moment(), 'day');

const sortKeys = (keyA: ApiKey, keyB: ApiKey): number => {
  const serialNumberCompare = keyA.serialNumber.localeCompare(keyB.serialNumber);
  if (serialNumberCompare !== 0) {
    return serialNumberCompare;
  }
  const expiresCompare = new Date(keyB.expires).getTime() - new Date(keyA.expires).getTime();
  if (expiresCompare !== 0) {
    return expiresCompare;
  }
  return keyA.id.localeCompare(keyB.id);
};

const KeysPage: FC = () => {
  const { Loader } = useLoader();
  const { getKeys, removeKey } = useApiClient();
  const { t } = useTranslation('portal');

  const [initStatus, setInitStatus] = useState<'initializing' | 'error' | 'initialized'>('initializing');
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [removeState, setRemoveState] = useState<{ id: string; status: 'confirming' | 'removing' } | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  useEffect(() => {
    const initialize = async () => {
      try {
        const keys = await getKeys();
        keys.sort(sortKeys);
        setApiKeys(keys);
        setInitStatus('initialized');
      } catch {
        setInitStatus('error');
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRemoveKey = (id: string) => {
    setRemoveState({ id, status: 'confirming' });
  };

  const onConfirmRemove = async () => {
    if (removeState) {
      const { id } = removeState;
      setRemoveState({ id, status: 'removing' });
      try {
        setRemoveError(null);
        await removeKey(id);
        setApiKeys((prev) => prev.filter((k) => k.id !== id));
      } catch {
        setRemoveError(t('portal.keys.keys-page.error.remove'));
      } finally {
        setRemoveState(null);
      }
    }
  };

  const columns: Column<ApiKey>[] = useMemo(
    () => [
      {
        accessor: 'description',
        Header: t('portal.keys.keys-page.table.description')
      },
      {
        accessor: 'serialNumber',
        Header: t('portal.keys.keys-page.table.serialnumber')
      },
      {
        accessor: 'key',
        Header: t('portal.keys.keys-page.table.key')
      },
      {
        accessor: 'expires',
        Header: t('portal.keys.keys-page.table.expires')
      },
      {
        accessor: 'revoked',
        Cell: ({ row }: { row: { original: ApiKey } }) => {
          const isExpired = !row.original.revoked && isKeyExpired(row.original.expires);
          const isInvalid = row.original.revoked || isExpired;
          return (
            <>
              {isInvalid && <span className="key-invalid" style={{ display: 'none' }} />}
              {row.original.revoked && (
                <Tooltip title={t('portal.keys.keys-page.table.revoked')}>
                  <WarningAmberOutlined color="error" fontSize="small" />
                </Tooltip>
              )}
              {isExpired && (
                <Tooltip title={t('portal.keys.keys-page.table.expired')}>
                  <EventBusyOutlined color="warning" fontSize="small" />
                </Tooltip>
              )}
            </>
          );
        }
      },
      {
        id: 'actions',
        Header: '',
        Cell: ({ row }: { row: { original: ApiKey } }) => (
          <Tooltip title={t('portal.keys.keys-page.remove')}>
            <IconButton size="small" color="error" onClick={() => onRemoveKey(row.original.id)}>
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      }
    ],
    [t]
  );

  const tableData = useCallback(
    async (): Promise<{ data: ApiKey[]; totalCount: number }> => ({
      data: apiKeys,
      totalCount: apiKeys.length
    }),
    [apiKeys]
  );

  if (initStatus === 'initializing') return <Loader />;
  if (initStatus === 'error') return <ErrorComponent text={t('portal.keys.keys-page.error.init')} center />;
  if (removeState?.status === 'removing') return <Loader />;

  return (
    <>
      <h3>{t('portal.keys.keys-page.title')}</h3>
      <Button variant="contained" color="primary" component={Link} to="/add-key" sx={{ marginBottom: 2 }}>
        {t('portal.keys.keys-page.addKey')}
      </Button>
      {removeError && <ErrorComponent text={removeError} />}
      <TableContainer>
        <Table columns={columns as Column<any>[]} fetchData={tableData} emptyDataElement={<p>{t('portal.keys.keys-page.noKeys')}</p>} />
      </TableContainer>
      <Dialog id="remove-key-dialog" open={removeState?.status === 'confirming'}>
        <Typography variant="body1" sx={{ margin: 2 }}>
          {t('portal.keys.keys-page.removeConfirm')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', margin: 2 }}>
          <Button variant="outlined" onClick={() => setRemoveState(null)}>
            {t('portal.keys.keys-page.removeCancel')}
          </Button>
          <Button variant="contained" color="error" onClick={onConfirmRemove}>
            {t('portal.keys.keys-page.removeConfirmButton')}
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

const TableContainer = styled(Box)(() => ({
  // Rows with invalid keys
  '& tr:has(.key-invalid) td:not(:nth-last-of-type(-n+2))': {
    opacity: 0.45,
    fontStyle: 'italic'
  }
}));

export default KeysPage;
